import { Confirm } from 'react-admin';
import type { AccessibleRegion } from '../auth/permissions';

/**
 * The cross-region confirmation (D-settings-s1, the hard rule "cross-region
 * operations require confirmation").
 *
 * A settings change that applies to BOTH regions is two audited per-region writes
 * (one per market row), each region legible in its own audit entry, NOT one opaque
 * all-write. Before committing them this NAMES the affected regions so the reach of
 * the change is explicit ("this applies to UK and US"). It reuses the same Confirm
 * dialog shell the status transitions use, with region-naming copy; the region
 * names are joined without an Oxford comma (two-region case reads "UK and US").
 *
 * This is deliberately generic (an action label plus a region list), so S2 / S3 /
 * S4 can reuse it for any both-region config change.
 */
export interface CrossRegionConfirmProps {
  isOpen: boolean;
  loading?: boolean;
  /** The regions the change will be written to, in order (e.g. ['UK', 'US']). */
  regions: readonly AccessibleRegion[];
  /** A short noun for the thing changing, e.g. 'site details'. */
  what: string;
  onConfirm: () => void;
  onClose: () => void;
}

/** Join region names for the copy: ['UK', 'US'] -> 'UK and US' (no Oxford comma). */
const nameRegions = (regions: readonly AccessibleRegion[]): string => {
  if (regions.length <= 1) return regions.join('');
  if (regions.length === 2) return `${regions[0]} and ${regions[1]}`;
  const head = regions.slice(0, -1).join(', ');
  return `${head} and ${regions[regions.length - 1]}`;
};

export const CrossRegionConfirm = ({
  isOpen,
  loading = false,
  regions,
  what,
  onConfirm,
  onClose,
}: CrossRegionConfirmProps) => {
  const names = nameRegions(regions);
  return (
    <Confirm
      isOpen={isOpen}
      loading={loading}
      title="Apply to both regions"
      content={`This change to the ${what} applies to ${names}. Each region is written and audited separately.`}
      confirm="Apply to both"
      cancel="Cancel"
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
};
